import { useCallback, useEffect, useMemo, useState } from 'react';
import cn from 'classnames';
import PropTypes from 'prop-types';
import { Typography, Table, Select } from 'antd';
import { SearchOutlined } from '@ant-design/icons';
import {
  UPLOAD_DOCUMENT_TRANSLATIONS,
  COMMON_LABELS,
} from 'const/translations';
import moment from 'moment';
import {
  DATE_FORMAT,
  SCREEN_WIDTH_BREAKPOINT_VALUE,
  STATUS_LOADING,
} from 'const';
import { Button } from 'components/ui/Button';
import { Input } from 'components/ui/Input';
import { RangePicker } from 'components/ui/DatePicker';
import { convertBytesToKilobytes } from 'helpers/common';
import MainStyles from 'assets/styles/main.module.css';
import { useDispatch, useSelector } from 'react-redux';
import { showActionConfirmationModal } from 'components/InvoiceSearch/modals/InfoModal/showActionConfirmationModal';
import {
  selectUserEmail,
  selectIsMainUser,
} from 'modules/loggedUserInfo/selectors';
import {
  moreUploadHistoryRequest,
  markDocumentAsMistakenAction,
} from 'modules/uploadDocuments/actions';

import iconCancel from 'assets/icons/icon-cancel.png';
import iconError from 'assets/icons/icon-error.png';

import Styles from './DocumentsHistory.module.css';
import { FileName } from '../FileName/FileName';

const { Option } = Select;

const {
  DOCUMENTS_HISTORY: {
    TITLE,
    SEARCH_BY_NAME,
    SEARCH_BY_USER,
    TABLE_HEADER_CAPTIONS: {
      FILE_NAME,
      TYPE,
      DATE,
      SIZE,
      FILE_SEND_BY,
      MISTAKE,
      STATUS,
    },
    STATUS_MISTAKE,
    STATUS_DEFAULT,
    CONFIRMATION_MISTAKE,
  },
  BUTTON_DOWNLOAD_MORE,
} = UPLOAD_DOCUMENT_TRANSLATIONS;

export const DocumentsHistory = ({
  documents,
  isHistoryDownloaded,
  isMobileVersion,
  uploadersHistory,
  searchDocuments,
  uploadHistoryStatus,
}) => {
  const dispatch = useDispatch();
  const isMainUser = useSelector(selectIsMainUser);
  const currentUserEmail = useSelector(selectUserEmail);

  const [fileName, setFileName] = useState();
  const [userNames, setUserNames] = useState(
    uploadersHistory.filter((e) => Boolean(e.UT_EMAIL))
  );
  const [userName, setUserName] = useState();
  const [range, setRange] = useState(undefined);

  const getMoreUploadHistory = useCallback(
    () =>
      dispatch(
        moreUploadHistoryRequest({
          fileName,
          range,
          userName,
        })
      ),
    [dispatch, fileName, range, userName]
  );

  const markDocumentAsMistaken = useCallback(
    (documentId) => {
      dispatch(
        markDocumentAsMistakenAction({
          documentId,
        })
      );
    },
    [dispatch]
  );

  const onMarkAsMistaken = (documentId, reportedMistake) => () => {
    if (reportedMistake) {
      return;
    }

    showActionConfirmationModal({
      handleOk: () => {
        markDocumentAsMistaken(documentId);
      },
      confirmationText: CONFIRMATION_MISTAKE,
    });
  };

  const onChangeFileName = (e) => {
    setFileName(e.target.value);
  };

  const onSearchUser = (search) => {
    setUserNames(
      search
        ? uploadersHistory.filter(
            (e) => Boolean(e.UT_EMAIL) && e.UT_EMAIL.includes(search)
          )
        : uploadersHistory.filter((e) => Boolean(e.UT_EMAIL))
    );
  };

  useEffect(() => {
    setUserNames(uploadersHistory.filter((e) => Boolean(e.UT_EMAIL)));
  }, [uploadersHistory]);

  useEffect(() => {
    searchDocuments({ fileName, userName, range });
  }, [fileName, range, searchDocuments, userName]);

  const columns = useMemo(
    () => [
      {
        title: FILE_SEND_BY,
        dataIndex: 'clientEmail',
        key: 'clientEmail',
        width: 250,
        render: (clientEmail) => <span>{clientEmail}</span>,
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: FILE_NAME,
        dataIndex: 'fileName',
        key: 'fileName',
        render: (fileName, { key, isDownloadable }) => (
          <FileName
            fileName={fileName}
            fileId={key}
            isDownloadable={isDownloadable}
          />
        ),
        width: 500,
        sorter: (a, b) => a.fileName.localeCompare(b.fileName),
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: TYPE,
        dataIndex: 'type',
        key: 'type',
        render: (type) => <span className={Styles.dateColumn}>{type}</span>,
        width: 100,
        sorter: (a, b) => a.fileName.localeCompare(b.fileName),
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: DATE,
        dataIndex: 'date',
        key: 'date',
        width: 130,
        render: (rawDate) => (
          <span className={Styles.dateColumn}>
            {moment(rawDate).format(DATE_FORMAT)}
          </span>
        ),
        sorter: (a, b) => moment(a.date).diff(moment(b.date)),
        defaultSortOrder: 'descend',
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: SIZE,
        dataIndex: 'size',
        key: 'size',
        width: 100,
        render: (rawSize) =>
          `${convertBytesToKilobytes(rawSize)} ${COMMON_LABELS.KB_SHORT}`,
      },
      {
        title: MISTAKE,
        dataIndex: 'mistaken',
        key: 'mistaken',
        width: 100,
        render: (reportedMistake, row) => (
          <div
            className={Styles.mistakeIconWrap}
            onClick={onMarkAsMistaken(row.key, reportedMistake)}
            style={{ cursor: reportedMistake ? 'default' : 'pointer' }}
          >
            <img src={reportedMistake ? iconError : iconCancel} />
          </div>
        ),
      },
      {
        title: STATUS,
        dataIndex: 'mistaken',
        key: 'mistaken',
        width: 100,
        render: (reportedMistake) => (
          <div>{reportedMistake ? STATUS_MISTAKE : STATUS_DEFAULT}</div>
        ),
      },
    ],
    []
  );

  return (
    <div className={Styles.wrapper}>
      <div className={Styles.history}>
        <Typography.Text className={Styles.title}>{TITLE}</Typography.Text>

        <div className={Styles.filter}>
          <div className={Styles.searchByNameWrapper}>
            <Input
              size="large"
              allowClear
              placeholder={SEARCH_BY_NAME}
              prefix={
                <SearchOutlined
                  size="large"
                  className={Styles.searchIconPrefix}
                />
              }
              onChange={onChangeFileName}
              value={fileName}
              className={cn(Styles.searchByName, {
                [Styles.searchByNameError]: fileName && fileName?.length < 3,
              })}
            />
            {fileName && fileName?.length < 3 && (
              <span className={Styles.searchByNameErrorText}>
                Merci de saisir au moins 3 caractères pour pouvoir lancer une
                recherche
              </span>
            )}
          </div>

          {(isMainUser || currentUserEmail.search('@exco') > -1) && (
            <Select
              value={userName}
              className={Styles.coloredDropdown}
              onChange={setUserName}
              classNames={{ popup: { root: Styles.dropdownMenu } }}
              onSearch={onSearchUser}
              placeholder={SEARCH_BY_USER}
              filterOption={false}
              showSearch
              getPopupContainer={() => document.getElementById('dropdown-area')}
              allowClear
            >
              {userNames.map(({ id, UT_EMAIL }) => (
                <Option value={id} className={Styles.option} key={id}>
                  {UT_EMAIL}
                </Option>
              ))}
            </Select>
          )}

          <RangePicker
            className={Styles.rangePicker}
            value={range}
            onChange={setRange}
          />
        </div>
      </div>

      <div className={Styles.historyTable}>
        <Table
          columns={columns}
          dataSource={documents}
          className={MainStyles.table}
          scroll={isMobileVersion ? { x: SCREEN_WIDTH_BREAKPOINT_VALUE } : {}}
          pagination={false}
          loading={uploadHistoryStatus === STATUS_LOADING}
        />
        {!isHistoryDownloaded && (
          <Button onClick={getMoreUploadHistory}>{BUTTON_DOWNLOAD_MORE}</Button>
        )}
      </div>
    </div>
  );
};

DocumentsHistory.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      fileName: PropTypes.string,
      size: PropTypes.number,
      type: PropTypes.string,
    })
  ),
  isHistoryDownloaded: PropTypes.bool,
  isMobileVersion: PropTypes.bool,
  uploadersHistory: PropTypes.any,
  searchDocuments: PropTypes.any,
  uploadHistoryStatus: PropTypes.bool,
};
